import { useEditableMode, UseEditableReturns, Editable, EditableGroup } from '@cad-arc';
import { useEffect, useMemo } from 'react';
import { FieldErrors, FieldValues, useFormState } from 'react-hook-form';
import { useIntegratedForm } from '../use-integrated-form';
import {
    FormInputHandleManagerScopeContextProvider,
    useFormInputHandleManagerScopeContext,
} from './form-input-handle-manager-scope-context';
import { v4 } from 'uuid';
import { EDITABLE_BORDER_RADIUS_STYLES_WHEN_HAS_EVENT_NOTIFICATION } from '../../event-notifications/event-notifications-field-indicator';

interface ErrorMap {
    [index: string]: Array<ErrorMap> | unknown;
}

/**
 * Specifically handles top level keys as well as nested array.
 * Does not handle the case where form fields just nested objects.
 */
function getErrorKeys(errorObj: ErrorMap, prefix = ''): string[] {
    return Object.entries(errorObj).flatMap(([key, value]) => {
        if (Array.isArray(value)) {
            return value.flatMap((entry, index) => {
                if (!entry) {
                    return [];
                }
                const entryPathSegment = `${key}.${index}`;
                return getErrorKeys(
                    entry,
                    prefix ? `${prefix}.${entryPathSegment}` : entryPathSegment
                );
            });
        }

        return [prefix ? `${prefix}.${key}` : key];
    });
}

const getFirstFormFieldWithError = ({
    errors,
    formFields,
}: {
    errors: FieldErrors<FieldValues> | undefined;
    formFields: string[];
}) => {
    const errorKeys = getErrorKeys(errors ?? {});
    return formFields.find((formField) => {
        return errorKeys.includes(formField);
    });
};

/**
 * This component will have the same prop interface as the arc `Editable` except that:
 *      - it doesn't support `children` -- only `editMode` and `viewMode`
 *      - `editMode` render prop for `toggleMode` can be undefined
 */
type TFormEditableProps = Omit<
    Extract<React.ComponentProps<typeof Editable>, { editMode: unknown }>,
    'editMode'
> & {
    editMode: (
        args: Omit<UseEditableReturns, 'containerProps' | 'toggleMode'> & {
            // toggleMode can be undefined (it'll be undefined if the form has errors)
            toggleMode?: (mode?: string) => void;
        }
    ) => React.ReactNode;
    hasNotifications?: boolean;
};

/**
 * An Editable component that will automatically focus the first form input
 * (or first field with an error) when put into `edit` mode
 *
 * Additionally, if there are field errors, this component
 * will automatically go into `edit` mode, and it will pass
 * `undefined` as the `toggleMode` render prop to `editMode`
 */
export const FormEditable = (props: TFormEditableProps) => {
    const scope = useMemo(() => {
        return v4();
    }, []);

    return (
        <FormInputHandleManagerScopeContextProvider value={scope}>
            <EditableInner {...props} />
        </FormInputHandleManagerScopeContextProvider>
    );
};

const EditableInner = (props: TFormEditableProps) => {
    const integratedForm = useIntegratedForm();
    const { errors } = useFormState();
    const inputHandleManager = integratedForm.inputHandleManager;
    const scope = useFormInputHandleManagerScopeContext();
    const formMode = integratedForm.mode;
    const firstFormFieldWithError = getFirstFormFieldWithError({
        errors,
        formFields: Object.keys(inputHandleManager.getHandlesForScope(scope)),
    });
    const editableHasErrors = !!firstFormFieldWithError;

    // Control the editable mode locally so that we can progamatically
    // set the mode from outside the editable component
    const [editableMode, setEditableMode] = useEditableMode('view');

    /**
     * When our editable has errors, put it into `edit` mode
     */
    useEffect(() => {
        if (editableHasErrors) {
            setEditableMode('edit');
        }
        // a bug in useEditableMode means we can't use setEditableMode as a dependency
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableHasErrors]);

    return (
        <EditableGroup mode={editableMode}>
            <Editable
                isPreviewInteractive={formMode === 'read-only' ? false : true}
                style={{
                    flexGrow: 1,
                    borderRadius: props.hasNotifications
                        ? EDITABLE_BORDER_RADIUS_STYLES_WHEN_HAS_EVENT_NOTIFICATION
                        : undefined,
                }}
                onModeChange={(mode) => {
                    // When enabling `edit` mode, focus the first input
                    // If there are errors, then focus the first input with errors
                    if (mode === 'edit') {
                        const formFields = inputHandleManager.getHandlesForScope(scope);

                        const formFieldToFocus = firstFormFieldWithError
                            ? formFields[firstFormFieldWithError]
                            : formFields[Object.keys(formFields)[0]];

                        if (formFieldToFocus && formFieldToFocus?.ref?.current) {
                            formFieldToFocus.ref.current.focus();
                        }
                    }
                }}
                {...props}
                editMode={({ toggleMode, ...params }) =>
                    props.editMode({
                        // If we have errors, don't allow us to toggle
                        toggleMode: editableHasErrors ? undefined : toggleMode,
                        ...params,
                    })
                }
            />
        </EditableGroup>
    );
};
